import { FC, useState, useEffect } from 'react';
import MainLayout from 'components/main-layout/MainLayout';
import { useTranslation } from 'react-i18next';
import LayoutContent from 'components/main-layout/LayoutContent';
import TabNavigation from 'components/TabNavigation';
import { usePermissionsContext } from 'store/permissions';
import { PermissionKeys } from 'store/types';
import { getSubdomain } from 'utils/helpers/routing';
import { useActiveClientContext } from 'store/active-client';
import { getUserInfo } from 'utils/auth';

interface IProps {
  isLoading?: boolean;
  error?: unknown;
  children: React.ReactNode;
}

const InnerWrapper: FC<IProps> = ({ children, isLoading, error }) => {
  const { checkPermission, permissions } = usePermissionsContext();
  const { client } = useActiveClientContext();
  const { t } = useTranslation();
  const userInfo = getUserInfo();
  const isOperatorMode = getSubdomain() === 'operators';

  const configureTabs = () => {
    let tabItems = [
      {
        to: '/reports/documents',
        label: t('reports.documents'),
        isExact: true,
      },
      {
        to: '/reports/documents-by-action',
        label: t('reports.documentsByAction'),
      },
    ];

    if (
      (userInfo.featureLeavesIsActive && checkPermission(PermissionKeys.CAN_MANAGE_LEAVES)) ||
      (isOperatorMode && client?.leaves)
    ) {
      tabItems = [
        ...tabItems,
        {
          to: '/reports/leaves-summary',
          label: t('reports.leavesDetailed'),
        },
        {
          to: '/reports/leaves-list',
          label: t('reports.leavesList'),
        },
        {
          to: '/reports/leaves-availability',
          label: t('reports.leavesAvailability'),
        },
      ];
    }

    if (isOperatorMode || checkPermission(PermissionKeys.CAN_DOWNLOAD_USER_DATA_REPORT)) {
      tabItems = [
        ...tabItems,
        {
          to: '/reports/employee-data',
          label: t('reports.employeeData'),
        },
      ];
    }

    if (!userInfo.featureLeavesIsActive && checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)) {
      tabItems = [
        ...tabItems,
        {
          to: '/reports/leaves',
          label: t('reports.leaves'),
        },
      ];
    }

    if (
      (userInfo.featureForm76 && checkPermission(PermissionKeys.CAN_MANAGE_LEAVES)) ||
      (isOperatorMode && client?.is_form_76)
    ) {
      tabItems = [
        ...tabItems,
        {
          to: '/reports/leaves-form-76',
          label: t('reports.leavesFormSixtySeven'),
        },
      ];
    }

    if (
      (userInfo.featureHourlyAbsences && checkPermission(PermissionKeys.CAN_MANAGE_HOURLY_ABSENCES)) ||
      (isOperatorMode && client?.is_hourly_absences)
    ) {
      tabItems = [
        ...tabItems,
        {
          to: '/reports/hourly-absences',
          label: t('reports.hourlyAbsences'),
        },
      ];
    }

    if (
      (userInfo.featureHealthAndSafetyTraining &&
        checkPermission(PermissionKeys.CAN_MANAGE_HEALTH_AND_SAFETY_TRAINING)) ||
      (isOperatorMode && client?.is_health_and_safety_training)
    ) {
      tabItems = [
        ...tabItems,
        {
          to: '/reports/health-and-safety-training',
          label: t('reports.healthAndSafetyTraining'),
        },
      ];
    }

    if (
      (userInfo.featureWorkTrips && checkPermission(PermissionKeys.CAN_MANAGE_WORK_TRIPS)) ||
      (isOperatorMode && client?.is_work_trips)
    ) {
      tabItems = [
        ...tabItems,
        {
          to: '/reports/work-trips',
          label: t('reports.workTrips'),
        },
      ];
    }

    const canAccessMunicipalityReport = checkPermission(PermissionKeys.CAN_ACCESS_MUNICIPALITY_REPORT);

    const isOperatorInMunicipality = getSubdomain() === 'operators' && client?.type === 'municipality';

    if (isOperatorInMunicipality || canAccessMunicipalityReport)
      tabItems = [
        ...tabItems,
        {
          to: '/reports/municipality',
          label: t('reports.municipality'),
        },
      ];

    return tabItems;
  };

  const [tabs, setTabs] = useState(configureTabs());

  useEffect(() => {
    setTabs(configureTabs());
  }, [permissions]);
  return (
    <MainLayout title={t('reports.title')}>
      <TabNavigation tabs={tabs} />
      <LayoutContent
        isLoading={isLoading}
        error={error}
      >
        {children}
      </LayoutContent>
    </MainLayout>
  );
};

export default InnerWrapper;
